import { FC } from 'react';
import { Box, Typography, Paper, Grid, Chip, Button, Divider, Avatar, Alert } from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  ExitToApp as ExitIcon,
} from '@mui/icons-material';
import { useAuth, useTranslations } from '@/hooks';

export const DashboardPage: FC = () => {
  const { user, logout, isEmailVerified } = useAuth();
  const { t } = useTranslations();

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">{t('dashboard.loading')}</Typography>
      </Box>
    );
  }

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('dashboard.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h6">{t('dashboard.profileInfo')}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('dashboard.name')}:</strong> {user.firstName} {user.lastName}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>{t('auth.email')}:</strong> {user.email}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<EmailIcon />}
                label={isEmailVerified ? t('dashboard.emailVerified') : t('dashboard.emailNotVerified')}
                color={isEmailVerified ? 'success' : 'warning'}
                size="small"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Security Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <SecurityIcon />
              </Avatar>
              <Typography variant="h6">{t('dashboard.securitySettings')}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('dashboard.securityMessage')}
            </Typography>

            {!isEmailVerified && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('dashboard.verifyEmailWarning')}
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" color="primary" sx={{ mr: 1, mb: 1 }}>
                {t('dashboard.changePassword')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.quickActions')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" color="primary">
                {t('dashboard.editProfile')}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ExitIcon />}
                onClick={handleLogout}
              >
                {t('auth.signOut')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
